/**
 * organization-chooser — the panel an `<organization-plate>` opens. The open
 * organization is its subject, never a pickable row; the person's open
 * invitations stand beneath it under "Invited to", then their other
 * organizations under "Switch to", and with none either group is absent. It
 * closes on the signed-in person and the one way out, the whole of what the
 * account needs here.
 *
 * The invitations are here because a member whose organization opens on
 * sign-in never passes through the organization entry, so an invitation
 * announced while they work has to be findable where their other
 * organizations already are. Accepting one opens it, and the organization
 * change closes this panel with every other overlay.
 *
 * Data down, intent up: the plate hands it the organizations, the switch's and
 * the sign-out's progress, and hears `organization-select` (detail: the id)
 * and `sign-out` (after confirm()) on this element. Whoever answers them
 * reports back through the same properties. The invitation rows read their
 * own cell and answer through their own channel.
 */
import { passkeySupported$ } from "@mhmo91/passkey/client";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm } from "@mhmo91/schmancy/overlay";
import { matchesSearch } from "@mhmo91/schmancy/utils";
import { html, type PropertyValues } from "lit";
import { property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { exhaustMap, filter, from, merge, Subject, takeUntil, tap } from "rxjs";
import { passkeyEnrolled, passkeyPhase } from "../auth/passkey.state.ts";
import { session } from "../auth/session.state.ts";
import { signAction$ } from "../auth/sign.state.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import "./invitation-row.view.ts";
import { invitationsOnScreen } from "./invitation.state.ts";
import { organizationInitials } from "./organization-plate.state.ts";

@schmancyElement("organization-chooser")
export class OrganizationChooser extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) organizations: readonly {
		readonly id: string;
		readonly name: string;
	}[] = [];
	@property({ attribute: false }) organizationId: string | null = null;
	@property({ attribute: false }) status: "loading" | "ready" | "error" =
		"ready";
	@property({ type: Boolean }) switching = false;
	@property() error = "";
	@property() email = "";
	@property({ type: Boolean }) signingOut = false;
	@property({ type: Boolean }) signOutFailed = false;

	@state() private passkeySupported = false;
	@state() private passkeyUid: string | null = null;
	@state() private enrolledUid: string | null = null;
	@state() private passkeyAttempt: "idle" | "working" | "done" | "error" =
		"idle";
	@state() private orgFilter = "";
	// Which row asked, so its own trailing slot carries the wait.
	@state() private picked: string | null = null;

	private readonly signOut$ = new Subject<void>();

	override connectedCallback() {
		super.connectedCallback();
		// exhaustMap: a second press while the confirm is open is dropped; once
		// it is answered the busy property guards the press instead.
		merge(
			passkeySupported$.pipe(
				tap((supported) => {
					this.passkeySupported = supported;
				}),
			),
			merge(session.$, passkeyEnrolled.$, passkeyPhase.$).pipe(
				tap(() => {
					this.passkeyUid =
						session.value.status === "member" ? session.value.uid : null;
					this.enrolledUid = passkeyEnrolled.value;
					this.passkeyAttempt = passkeyPhase.value;
				}),
			),
			this.signOut$.pipe(
				exhaustMap(() =>
					from(
						confirm({
							title: this.t("admin.nav.signOutConfirmTitle"),
							message: this.t("admin.nav.signOutConfirmMessage"),
							confirmText: this.t("admin.nav.signOut"),
							cancelText: this.t("admin.common.cancel"),
							variant: "danger",
						}),
					),
				),
				filter((confirmed) => confirmed === true),
				tap(() => this.dispatchEvent(new CustomEvent("sign-out"))),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override updated(changed: PropertyValues<this>) {
		// A refused switch answers at the row that asked: focus goes back to it
		// once the refusal has rendered beneath it.
		if (
			changed.get("switching") === true &&
			!this.switching &&
			this.error !== ""
		)
			for (const row of this.renderRoot.querySelectorAll<HTMLElement>(
				"schmancy-list-item[data-org]",
			))
				if (row.dataset.org === this.picked) row.focus();
		// A sign-out that ran and was not refused has landed.
		if (
			changed.get("signingOut") === true &&
			!this.signingOut &&
			!this.signOutFailed
		)
			this.dispatchEvent(
				new CustomEvent("close", { bubbles: true, composed: true }),
			);
	}

	override render() {
		const plates = this.organizations.map((organization) => ({
			...organization,
			initials: organizationInitials(organization.name),
		}));
		const active =
			plates.find((plate) => plate.id === this.organizationId) ?? null;
		const allOthers = plates.filter(
			(plate) => plate.id !== this.organizationId,
		);
		// Scanning the list stays the faster path below this count.
		const filterShown = plates.length > 6;
		const query = this.orgFilter.trim().toLowerCase();
		const others =
			filterShown && query.length > 0
				? allOthers.filter((org) => matchesSearch(query, [org.name]))
				: allOthers;
		const invited = invitationsOnScreen.get();

		return html`
			<div class="grid w-md max-w-full max-h-[80svh] grid-rows-[minmax(0,auto)_auto_auto] gap-4 py-3">
				<schmancy-visually-hidden role="status" aria-live="polite"
					>${this.status === "loading" ? this.t("admin.organizations.reading") : ""}</schmancy-visually-hidden
				>

				<div class="grid content-start gap-4 min-h-0 overflow-auto">
					${when(
						this.status === "loading",
						() => html`
							<div class="grid gap-4" aria-hidden="true">
								<div class="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 px-4">
									<schmancy-skeleton class="w-10 h-10"></schmancy-skeleton>
									<schmancy-skeleton shape="text" class="w-2/5 text-title-md"></schmancy-skeleton>
								</div>
								<div class="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 px-4 py-2">
									<schmancy-skeleton class="w-8 h-8"></schmancy-skeleton>
									<schmancy-skeleton shape="text" class="w-1/2 text-body-md"></schmancy-skeleton>
								</div>
							</div>
						`,
						() => html`
							${when(
								active,
								(subject) => html`
									<div class="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 px-4">
										<schmancy-avatar
											aria-hidden="true"
											shape="square"
											color="neutral"
											class="w-10 h-10"
											initials=${subject.initials}
										></schmancy-avatar>
										<schmancy-typography type="title" token="md" class="block truncate">${subject.name}</schmancy-typography>
									</div>
								`,
							)}
							${when(
								invited.length > 0,
								() => html`
									<div role="group" aria-labelledby="invited-to" class="grid gap-1">
										<schmancy-typography
											id="invited-to"
											type="label"
											token="sm"
											class="text-surface-onVariant px-4 block"
											>${this.t("admin.organizations.invitedTo")}</schmancy-typography
										>
										<div class="grid gap-2 px-2 sm:px-1">
											${repeat(
												invited,
												(row) => row.orgId,
												(row) =>
													html`<invitation-row .orgId=${row.orgId}></invitation-row>`,
											)}
										</div>
									</div>
								`,
							)}
							${when(
								allOthers.length > 0,
								() => html`
									<div role="group" aria-labelledby="switch-to" class="grid gap-1">
										<schmancy-typography
											id="switch-to"
											type="label"
											token="sm"
											class="text-surface-onVariant px-4 block"
											>${this.t("admin.organizations.switchTo")}</schmancy-typography
										>
										${when(
											filterShown,
											() => html`
												<schmancy-input
													class="px-4"
													size="sm"
													placeholder=${this.t("admin.organizations.search")}
													aria-label=${this.t("admin.organizations.search")}
													.value=${this.orgFilter}
													@input=${(e: CustomEvent<{ value: string }>) => {
														this.orgFilter = e.detail?.value ?? "";
													}}
												></schmancy-input>
											`,
										)}
										<div class="grid gap-0.5 px-2 sm:px-1">
											${repeat(
												others,
												(org) => org.id,
												(org) => html`
													<schmancy-list-item
														data-org=${org.id}
														rounded
														class="select-none"
														aria-busy=${this.switching && this.picked === org.id ? "true" : "false"}
														@click=${() => {
															if (this.switching) return;
															this.picked = org.id;
															this.dispatchEvent(
																new CustomEvent("organization-select", {
																	detail: org.id,
																}),
															);
														}}
													>
														<schmancy-avatar
															slot="leading"
															aria-hidden="true"
															shape="square"
															color="neutral"
															class="w-8 h-8 shrink-0"
															initials=${org.initials}
														></schmancy-avatar>
														<schmancy-typography type="body" token="md" class="block truncate">${org.name}</schmancy-typography>
														<span slot="trailing" class="w-5 h-5 shrink-0 grid place-items-center">
															${when(
																this.switching && this.picked === org.id,
																() =>
																	html`<schmancy-spinner size="sm" aria-hidden="true"></schmancy-spinner>`,
															)}
														</span>
													</schmancy-list-item>
													${when(
														this.error && this.picked === org.id,
														() => html`
															<schmancy-typography role="status" type="body" token="sm" class="text-error-default px-4 block"
																>${this.error}</schmancy-typography
															>
														`,
													)}
												`,
											)}
										</div>
										${when(
											filterShown && query.length > 0 && others.length === 0,
											() => html`
												<schmancy-typography type="body" token="sm" class="text-surface-onVariant px-4 block"
													>${this.t("admin.organizations.noMatch", { query: this.orgFilter })}</schmancy-typography
												>
											`,
										)}
									</div>
								`,
							)}
						`,
					)}
				</div>

				${when(
					this.error && !allOthers.some((org) => org.id === this.picked),
					() => html`
						<schmancy-typography role="status" type="body" token="sm" class="text-error-default px-4 block"
							>${this.error}</schmancy-typography
						>
					`,
				)}

				<div role="group" aria-label=${this.t("admin.nav.account")} class="grid gap-1 px-2 sm:px-1">
					<!-- Identity takes the flexible column and the one action keeps its
					     own; the address wraps rather than clipping, since a cut email
					     no longer says who is signed in. -->
					<div class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-2 sm:px-3 py-1">
						<div class="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 min-w-0">
							<schmancy-avatar
								aria-hidden="true"
								name=${this.email}
								initials=${(this.email || "?").charAt(0).toUpperCase()}
								class="w-8 h-8"
							></schmancy-avatar>
							<schmancy-typography type="body" token="md" weight="medium" class="block break-all"
								>${this.email}</schmancy-typography
							>
						</div>
						<div class="flex items-center gap-0.5">
							${when(
								this.passkeySupported && this.passkeyUid,
								() => html`
								<passkey-enroll density="utility" language=${this.uiLanguage}
									.phase=${this.passkeyAttempt === "idle" || this.passkeyAttempt === "done" ? (this.enrolledUid === this.passkeyUid ? "done" : "idle") : this.passkeyAttempt}
									@passkey-enroll=${() => signAction$.next({ kind: "enrollPasskey", requestId: crypto.randomUUID() })}></passkey-enroll>
							`,
							)}
						<schmancy-icon-button
							data-sign-out
							icon="logout"
							variant="text"
							color="neutral"
							title=${this.t("admin.nav.signOut")}
							aria-label=${this.t("admin.nav.signOut")}
							?busy=${this.signingOut}
							@click=${() => {
								if (this.signingOut) return;
								this.signOut$.next();
							}}
						></schmancy-icon-button>
						</div>
					</div>
					${when(
						this.signOutFailed,
						() => html`
							<schmancy-typography role="status" type="body" token="sm" class="text-error-default px-2 sm:px-3 block"
								>${this.t("admin.nav.signOutFailed")}</schmancy-typography
							>
						`,
					)}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"organization-chooser": OrganizationChooser;
	}
}
